// 🔗 Servicio para llamar al backend como puente con Supabase
// El backend usa SERVICE_ROLE_KEY para evitar problemas de RLS y timeout

import { supabase } from './supabaseClient';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export interface BackendResponse<T> {
  success: boolean;
  data: T;
  cached?: boolean;
  message?: string;
}

export interface GastoFilters {
  fechaInicio?: string;
  fechaFin?: string;
  categoria?: string;
  trabajador?: string;
}

// GET /api/gastos - Obtener gastos con filtros
export async function getGastosFromBackend(filters?: GastoFilters): Promise<BackendResponse<any[]>> {
  if (!BACKEND_URL) {
    throw new Error('Backend URL no configurada');
  }

  try {
    const queryParams = new URLSearchParams();
    if (filters?.fechaInicio) queryParams.append('fechaInicio', filters.fechaInicio);
    if (filters?.fechaFin) queryParams.append('fechaFin', filters.fechaFin);
    if (filters?.categoria) queryParams.append('categoria', filters.categoria);
    if (filters?.trabajador) queryParams.append('trabajador', filters.trabajador);

    const url = `${BACKEND_URL}/api/gastos${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    
    console.log('🔍 [BackendAPI] Llamando a:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Backend error: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error: any) {
    console.error('❌ [BackendAPI] Error al obtener gastos:', error);
    throw error;
  }
}

// POST /api/gastos - Crear nuevo gasto
export async function createGastoInBackend(gasto: any): Promise<BackendResponse<any>> {
  if (!BACKEND_URL) {
    throw new Error('Backend URL no configurada');
  }

  try {
    console.log('🔍 [BackendAPI] Creando gasto en backend...');
    
    const response = await fetch(`${BACKEND_URL}/api/gastos`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(gasto)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Backend error: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error: any) {
    console.error('❌ [BackendAPI] Error al crear gasto:', error);
    throw error;
  }
}

// GET /api/profiles - Obtener perfiles
export async function getProfilesFromBackend(): Promise<BackendResponse<any[]>> {
  if (!BACKEND_URL) {
    throw new Error('Backend URL no configurada');
  }

  try {
    const url = `${BACKEND_URL}/api/profiles`;
    console.log('🔍 [BackendAPI] Llamando a:', url);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Backend error: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error: any) {
    console.error('❌ [BackendAPI] Error al obtener profiles:', error);
    throw error;
  }
}

// GET /api/profiles/:id - Obtener perfil por ID
export async function getProfileByIdFromBackend(userId: string): Promise<BackendResponse<any>> {
  if (!BACKEND_URL) {
    throw new Error('Backend URL no configurada');
  }

  try {
    const url = `${BACKEND_URL}/api/profiles/${userId}`;
    console.log('🔍 [BackendAPI] Llamando a:', url);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Backend error: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error: any) {
    console.error('❌ [BackendAPI] Error al obtener perfil por ID:', error);
    throw error;
  }
}

// GET /api/storage/signed-url - Obtener URL firmada para un archivo en Storage
export async function getSignedUrlFromBackend(
  bucket: string,
  path: string,
  expiresIn: number = 14400
): Promise<BackendResponse<{ signedUrl: string; bucket: string; path: string }>> {
  if (!BACKEND_URL) {
    throw new Error('Backend URL no configurada');
  }

  try {
    const url = `${BACKEND_URL}/api/storage/signed-url?bucket=${encodeURIComponent(bucket)}&path=${encodeURIComponent(path)}&expiresIn=${expiresIn}`;
    console.log('🔍 [BackendAPI] Llamando a:', url);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Backend error: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error: any) {
    console.error('❌ [BackendAPI] Error al obtener URL firmada:', error);
    throw error;
  }
}

// PATCH /api/gastos/:id/alerta - Actualizar estado de alerta
export async function updateAlertaEstado(
  gastoId: string, 
  estado: 'pendiente' | 'aprobado' | 'rechazado'
): Promise<BackendResponse<any>> {
  // ✅ FALLBACK: Si backend no está disponible, usar Supabase directo
  if (!BACKEND_URL) {
    console.log('ℹ️ [BackendAPI] Backend URL no configurada, usando Supabase directo');
    
    try {
      console.log(`🔍 [BackendAPI] Actualizando estado de alerta del gasto ${gastoId} a: ${estado} (Supabase directo)`);
      
      // ✅ CRÍTICO: Agregar timeout para evitar que se quede colgado por RLS
      const TIMEOUT_MS = 10000; // 10 segundos
      
      const updateQuery = supabase
        .from('gastos')
        .update({ 
          estado_alerta: estado,
          fecha_modificacion_alerta: new Date().toISOString()
        })
        .eq('id', gastoId)
        .select()
        .single();
      
      const updateTimeout = new Promise<{ data: null, error: { message: string; code?: string } }>((_, reject) => {
        setTimeout(() => reject(new Error('Timeout al actualizar estado de alerta (10s)')), TIMEOUT_MS);
      });
      
      let data, error;
      try {
        const result = await Promise.race([updateQuery, updateTimeout]) as any;
        data = result?.data;
        error = result?.error;
      } catch (timeoutError: any) {
        if (timeoutError?.message?.includes('Timeout')) {
          console.error('❌ [BackendAPI] Timeout al actualizar estado de alerta - posible problema de RLS o conectividad');
          console.error('💡 [BackendAPI] Verifica las políticas RLS de la tabla "gastos" en Supabase');
          throw new Error('La operación tardó demasiado. Verifica las políticas RLS de la tabla "gastos" en Supabase.');
        }
        throw timeoutError;
      }
      
      if (error) {
        console.error('❌ [BackendAPI] Error al actualizar estado de alerta en Supabase:', error);
        
        // ✅ Manejar errores RLS específicamente
        if (error.code === 'PGRST301' || 
            error.message?.includes('permission') || 
            error.message?.includes('policy') ||
            error.message?.includes('RLS')) {
          console.error('⚠️ [BackendAPI] Error de permisos RLS detectado');
          console.error('💡 [BackendAPI] Verifica que exista una política RLS que permita:');
          console.error('   UPDATE en gastos para usuarios autenticados');
          throw new Error('Error de permisos al actualizar estado de alerta. Verifica las políticas RLS en Supabase.');
        }
        
        throw new Error(error.message || 'Error al actualizar estado de alerta');
      }
      
      console.log(`✅ [BackendAPI] Estado de alerta actualizado exitosamente en Supabase`);
      
      return {
        success: true,
        data: data
      };
    } catch (error: any) {
      console.error('❌ [BackendAPI] Error al actualizar estado de alerta:', error);
      throw error;
    }
  }

  try {
    console.log(`🔍 [BackendAPI] Actualizando estado de alerta del gasto ${gastoId} a: ${estado}`);
    
    // ✅ CRÍTICO: Agregar timeout también para la llamada al backend
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 segundos
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/gastos/${gastoId}/alerta`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ estado_alerta: estado }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Backend error: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (fetchError: any) {
      clearTimeout(timeoutId);
      if (fetchError.name === 'AbortError') {
        throw new Error('Timeout: La operación tardó demasiado');
      }
      throw fetchError;
    }
  } catch (error: any) {
    console.error('❌ [BackendAPI] Error al actualizar estado de alerta:', error);
    throw error;
  }
}

